import type { Point } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedValue } from '@softgames/localization';
import type { CollectionEventConfig } from '../types';
import { CollectEventInGameTrayLayout } from '../../../../layouts/collectEvent/CollectEventInGameTrayLayout';
import { CollectionTokenIconView } from './CollectionTokenIconView';
import { preloadCollectionEventAssets } from './preloadCollectionEventAssets';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { waitForMs } from '../../../utils/async-utils';
import { triggerParticleFX } from '../../../libs/particles/triggerParticleFX';
import { animateColorTo } from '../../../utils/pixi/animations/filter-animation-utils';
import { interpolationMap } from '../../../libs/TweenManager/interpolations';

export interface CollectionEventInGameTrayConfig {
  eventConfig: CollectionEventConfig;
}

export class CollectionEventInGameTrayView extends Container {
  public readonly onTrayOpened = new Signal();

  public readonly onTrayClosed = new Signal();

  private readonly layout = new CollectEventInGameTrayLayout();

  private tokenIconView: CollectionTokenIconView | null = null;

  private isExpanded = false;

  private isCollectingToken = false;

  private collectedAmount = 0;

  constructor(private readonly config: CollectionEventInGameTrayConfig) {
    super();
    void this.init();
  }

  private async init(): Promise<void> {
    const { eventConfig, eventConfig: { visuals: eventVisuals } } = this.config;

    await preloadCollectionEventAssets(eventVisuals);

    const { root, tokenIconSlot, arrow } = this.layout;

    root.props({
      parent: this,
    });

    arrow.on('pointertap', () => {
      if (this.isCollectingToken || this.isExpanded) return;

      void this.open();
    });

    // Whenever the user taps the screen, close the tray if open without blocking input on other elements
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    this.tokenIconView = new CollectionTokenIconView(eventVisuals).props({
      parent: tokenIconSlot,
    });

    this.collectedAmount = eventConfig.tempTokens;
    this.updateAmountText();
  }

  private readonly onDocumentPointerDown = (): void => {
    if (this.isCollectingToken) return;

    void this.close();
  };

  public async open(): Promise<void> {
    if (this.isExpanded) return;

    await playLayoutAnimation(this.layout, 'Open');

    this.isExpanded = true;

    this.onTrayOpened.emit();
  }

  public async close(): Promise<void> {
    if (!this.isExpanded) return;

    await playLayoutAnimation(this.layout, 'Close');

    this.isExpanded = false;

    this.onTrayClosed.emit();
  }

  public updateAmountText(): void {
    const { amountText } = this.layout;

    amountText.text = getLocalizedValue(this.collectedAmount);
  }

  public getAnimationTargetGlobalPosition(): Point {
    return this.layout.animationTargetSlot.getGlobalPosition();
  }

  public async animateAddCollectedTokens(amount: number, sourceGlobalPosition: Point): Promise<void> {
    const { trayContainer, particleSlot } = this.layout;

    this.isCollectingToken = true;

    const eventToken = await this.showToken(amount, sourceGlobalPosition);

    if (!this.isExpanded) {
      await this.open();
    }

    await this.animateTokenToTarget(eventToken);

    void triggerParticleFX('Reward_Tray_Particles', particleSlot);
    void trayContainer.tweenTo({ scaleXY: 1.05 }, 150, 'quadOut', true, 100, 0, true).asyncOnComplete();

    if (this.tokenIconView) animateColorTo(this.tokenIconView, 0xffffff, 0, 0.6, 200, 'quadIn', 0, 0, true);
    this.collectedAmount += amount;
    this.updateAmountText();

    await waitForMs(2000);
    await this.close();

    this.isCollectingToken = false;
  }

  private async showToken(amount: number, sourceGlobalPosition: Point): Promise<CollectionTokenIconView> {
    const { visuals: eventVisuals } = this.config.eventConfig;
    const eventToken = new CollectionTokenIconView(eventVisuals);

    const localSource = this.toLocal(sourceGlobalPosition);

    eventToken.props({
      parent: this,
      x: localSource.x,
      y: localSource.y,
      scaleXY: 1.2,
    });

    await eventToken.animateCollect(amount);

    return eventToken;
  }

  private async animateTokenToTarget(eventToken: CollectionTokenIconView): Promise<void> {
    const targetPosition = this.layout.animationTargetSlot.position;
    const midPoint = (eventToken.x + targetPosition.x) * 0.5;

    await eventToken
      .tweenTo(
        {
          x: [midPoint + 300, targetPosition.x],
          y: [midPoint - 300, targetPosition.y],
          scaleXY: 0.2,
        },
        600,
        'quadIn',
        true,
      )
      .interpolation(interpolationMap.bezier)
      .asyncOnComplete();

    eventToken.destroy({ children: true });
  }

  public destroy(options?: { children?: boolean }): void {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    super.destroy(options);
  }
}
